import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FormControl,
  Box, TextField, Button, Typography,Alert,
  CircularProgress, Paper, InputAdornment, Stack, LinearProgress,
  Snackbar, IconButton
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { grey, red,blue, orange, yellow, green } from '@mui/material/colors';

import BASE_URL from '../../config'

const contrastBlue = '#A9C7E3'; // Azul Acero Claro

// --- COMPONENTE DEL INDICADOR DE FUERZA ---
const PasswordStrengthIndicator = ({ password }) => {
  const [isVisible, setIsVisible] = React.useState(true);
  const requirements = [
    { label: 'Mínimo 8 caracteres', test: (p) => p.length >= 8 },
    { label: 'Una mayúscula (A-Z)', test: (p) => /[A-Z]/.test(p) },
    { label: 'Una minúscula (a-z)', test: (p) => /[a-z]/.test(p) },
    { label: 'Un número (0-9)', test: (p) => /[0-9]/.test(p) },
  ];
  const score = requirements.filter(req => req.test(password)).length;
  const isFull = score === 4;
  
  React.useEffect(() => {
    if (isFull) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(true); // Si borra algo y deja de ser seguro, vuelve a aparecer
    }
  }, [isFull]);

  // Configuración de la barra según el progreso
  const getBarColor = () => {
    if (score <= 1) return red[500];
    if (score === 2) return orange[500];
    if (score === 3) return yellow[700];
    return green[500];
  };

  if (!isVisible) return null;

  return (
    <Box sx={{ my:2, mb:3, bgcolor:grey[100], borderRadius: 2, border: '1px solid #eee' }}>
      <Typography variant='body1' sx={{ display: 'block', mx: 2, color: grey[800] }}>
        Fortaleza de la contraseña:
      </Typography>
      
      {/* Barra de Progreso */}
      <LinearProgress 
        variant="determinate" 
        value={(score / 4) * 100} 
        sx={{ 
          height: 6, 
          borderRadius: 3, 
          mx: 2, 
          bgcolor: grey[200],
          '& .MuiLinearProgress-bar': { bgcolor: getBarColor() }
        }} 
        aria-disabled
      />
      <Stack spacing={0.2} sx={{mx:2,my:1}}>
        {requirements.map((req, index) => {
          const isMet = req.test(password);
          return (
            <Stack key={index} direction="row" alignItems="center" spacing={1}>
              {isMet ? (
                <CheckCircleIcon sx={{ fontSize: 14, color: green[600] }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: grey[400] }} />
              )}
              <Typography variant="caption" sx={{ color: isMet ? green[800] : grey[600] }}>
                {req.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
    
  );
};

// --- COMPONENTE PRINCIPAL ---
export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const mostrarNotificacion = (msg, sev = 'success') => setSnackbar({ open: true, message: msg, severity: sev });

  // Validación lógica para habilitar el botón
  const isPasswordValid = useMemo(() => {
    return password.length >= 8 && 
      /[A-Z]/.test(password) && 
      /[a-z]/.test(password) && 
      /[0-9]/.test(password);
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      return mostrarNotificacion('Las contraseñas no coinciden.', 'error');
    }

    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const data = await res.json();
      if (res.ok) {
        mostrarNotificacion('¡Contraseña actualizada! Redirigiendo...', 'success');
        setTimeout(() => navigate('/home'), 3000);
      } else {
        mostrarNotificacion(data.message || 'Error al actualizar.', 'error');
      }
    } catch (error) {
      mostrarNotificacion('Error de conexión con el servidor.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const textFieldStyle = {
      '& .MuiOutlinedInput-root': {
        bgcolor: '#f5f5f5',
        borderRadius: '8px',
        '& fieldset': { borderColor: 'transparent' },
        '&:hover fieldset': { borderColor: grey[400] },
        '&.Mui-focused fieldset': { borderColor: grey[600] },
      },
    };

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      bgcolor: '#F8F9FA', 
      backgroundImage: `radial-gradient(at 0% 0%, hsla(210,100%,95%,1) 0, transparent 50%), 
                        radial-gradient(at 100% 100%, hsla(210,100%,90%,1) 0, transparent 50%)`,
      '@keyframes fadeInUp': {
        from: { opacity: 0, transform: 'translateY(20px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      }
    }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 5, 
          borderRadius: 9, 
          width: '90%', 
          maxWidth: 420,
          zIndex: 1,
          position: 'relative',

          bgcolor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(20px) saturate(140%)',
          
          // El Borde: Usamos el azul acero para que combine con los grises
          border: `1.5px solid ${contrastBlue}`, 
          
          // Sombra multinivel para dar profundidad real
          boxShadow: `
            0 4px 6px -1px rgba(0, 0, 0, 0.05), 
            0 20px 25px -5px rgba(0, 0, 0, 0.08),
            inset 0 0 15px rgba(255, 255, 255, 0.6)
          `,
        }}
      >
        <Box textAlign="center" mb={1}>
          <IconButton disableRipple sx={{ my:2, pointerEvents: "none", color: "white", transform: "scale(1.2)", backgroundColor: "black" }}>
            <LockPersonRoundedIcon />
          </IconButton>
          <Typography variant="h6" margin='10px 0'>
            Restablecer Contraseña
          </Typography>
          <Typography variant="body1" sx={{ mx: 3, mb: 2 }} textAlign="center" color="Grey">
            Crea una contraseña nueva para ingresar de nuevo a tu cuenta.
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{display:'flex',flexDirection:'column',gap:2}}>
            <FormControl fullWidth>
              <Typography>Contraseña Nueva
              </Typography>
              <TextField
              fullWidth
              size="small" 
              type="password"
              placeholder="Nueva Contraseña"
              variant="outlined"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={textFieldStyle}
              slotProps={{
                input: {
                startAdornment: (
                  <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment>
                ),
                }
              }}
            />

            </FormControl>

            {/* Indicador de fuerza dinámico */}
            {password.length > 0 && <PasswordStrengthIndicator password={password} />}

            <FormControl fullWidth>
              <Typography>Confirmar contraseña
              </Typography>
            <TextField
              size="small"
              fullWidth
              type="password"
              variant="outlined"
              placeholder="Nueva Contraseña"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={textFieldStyle}
              slotProps={{
                input: {
                startAdornment: (
                  <InputAdornment position="start"><LockOutlinedIcon fontSize="small" /></InputAdornment>
                ),
                }
              }}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={confirmPassword.length > 0 && password !== confirmPassword ? "Las contraseñas no coinciden" : ""}
            />
            </FormControl>

            <Button
              size="large"
              variant="contained"
              fullWidth
              type="submit"
              disabled={loading || !isPasswordValid || password !== confirmPassword}
              sx={{ borderRadius:'8px', bgcolor:'black', textTransform:'none' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Actualizar Contraseña'}
            </Button>
          </Box>
        </form>
      </Paper>
      <Snackbar 
        open={snackbar.open} autoHideDuration={2000} 
                  onClose={() => setSnackbar({...snackbar, open: false})} 
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '10px' }}>
            {snackbar.message}
          </Alert>
      </Snackbar>
    </Box>
  );
}